import { useState, useEffect, useMemo } from 'react';

import SaveButton from '../../components/psc/lib/SaveButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import { Radio } from '../../components/psc/lib/Radio'
import CancelButton from '../../components/psc/lib/CancelButton'
import TextArea from '../../components/psc/lib/TextArea'

import useClassifications from '../../hooks/useClassifications'

const Classifications = () => {
    const [classifications, setClassifications] = useState([])
    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [error, setError] = useState([])
    
    const {
        reloadClassifications,
    } = useClassifications()

    const activeClassifications = useMemo(() =>
        classifications.filter(({ is_archived }) => !is_archived)
    , [classifications]);

    const archivedClassifications = useMemo(() =>
        classifications.filter(({ is_archived }) => is_archived)
    , [classifications]);

    const noDefault = useMemo(() =>
        activeClassifications.every(({ is_default }) => !is_default)
    , [activeClassifications]);
    
    useEffect(() => {
        fetch('/v0/classifications', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then((json) => {
                const no_default = json.results.every((m) => !m.is_default);

                const result = json.results.map((m) => ({
                    ...m,
                    timestamp: m.id, // just to use same key 'timestamp' for all, including new, entries
                }))

                const activeClassifications = result
                    .filter(({ is_archived }) => !is_archived)
                    .sort((a, b) => a.internal_order - b.internal_order);

                const archivedClassifications = result
                    .filter(({ is_archived }) => is_archived)
                    .sort((a, b) => a.internal_order - b.internal_order);

                const classificationsSorted = [...activeClassifications, ...archivedClassifications]
                    .map((m, i) => ({ ...m, internal_order: i }));

                setClassifications(classificationsSorted);
            })
    }, [])

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive ) {
            return null
        }

        let errors = []

        const uniq = classifications
            .map((m) => {
                return { count: 1, name: m.name }
            })
            .reduce((a, b) => {
                a[b.name] = (a[b.name] || 0) + b.count
                return a
            }, {})
        let duplicates = Object.keys(uniq).filter((a) => uniq[a] > 1)
        if (duplicates.length > 0) {
            errors.push('The following Classification Levels have been specified more than once: ' + duplicates.join(', '))
        }
        
        if ( errors.length > 0 ) {
            setError(errors)
            return null
        }

        let internal_order = 1
        classifications.map((m) => {
            m.internal_order = internal_order++
        })
        
        setSubmitActive(true)
        setSubmitSpinner(true)
        let submit = {
            objType: 'classifications',
            data: classifications,
            successCallback: function() {
                reloadClassifications()
            }
        }
        return submit
    }

    const moveMemberUp = (member) => {
        const updated = activeClassifications
            .map((m) => {
                // swap updated member order with previous member
                if (m.internal_order === member.internal_order) {
                    return { ...m, internal_order: member.internal_order - 1 };
                }

                // swap previous member order with updated member
                if (m.internal_order === member.internal_order - 1) {
                    return { ...m, internal_order: member.internal_order };
                }

                // keep member as is
                return m;
            })
            .sort((a, b) => a.internal_order - b.internal_order);

        setClassifications([...updated, ...archivedClassifications]);
    }

    const moveMemberDown = (member) => {
        const updated = activeClassifications
            .map((m) => {
                // swap updated member order with next member
                if (m.internal_order === member.internal_order) {
                    return { ...m, internal_order: member.internal_order + 1 };
                }

                // swap next member order with updated member
                if (m.internal_order === member.internal_order + 1) {
                    return { ...m, internal_order: member.internal_order };
                }

                // keep member as is
                return m;
            })
            .sort((a, b) => a.internal_order - b.internal_order);

        setClassifications([...updated, ...archivedClassifications]);
    }

    const deleteMember = (member) => {
        const updated = classifications
            .filter(c => c.timestamp !== member.timestamp)
            .map((m, i) => ({ ...m, internal_order: i }));

        setClassifications(updated);
    }

    const archiveMember = (member) => {
        const updatedActiveClassifications = activeClassifications.filter((m) => m.timestamp !== member.timestamp);
        const updatedArchivedClassifications = [{ ...member, is_archived: true }, ...archivedClassifications];
        const updated = [...updatedActiveClassifications, ...updatedArchivedClassifications]
            .map((m, i) => ({ ...m, internal_order: i, is_default: false }));

        setClassifications(updated);
    }

    const restoreMember = (member) => {
        const updatedActiveClassifications = [...activeClassifications, { ...member, is_archived: false }];
        const updatedArchivedClassifications = archivedClassifications.filter((m) => m.timestamp !== member.timestamp);
        const updated = [...updatedActiveClassifications, ...updatedArchivedClassifications]
            .map((m, i) => ({ ...m, internal_order: i }));

        setClassifications(updated);
    }

    const addMember = () => {
        const newMember = {
            name:'',
            description: '',
            is_default: false,
            is_archived: false,
            timestamp: Date.now(),
            internal_order: activeClassifications.length,
        }

        const updatedArchivedClassifications = archivedClassifications
            .map((m) => ({ ...m, internal_order: m.internal_order + 1 }));

        setClassifications([...activeClassifications, newMember, ...updatedArchivedClassifications]);
    }

    const updateMember = (event, index, field) => {
        const updated = classifications.map((m, i) => (
            i === index ? { ...m, [field]: event.target.value } : m
        ));

        setClassifications(updated);
    }
    const setDefault = ({ target: { value } }) => {
        const updated = classifications.map((m) => ({
            ...m,
            is_default: Number(value) === m.timestamp,
        }));

        setClassifications(updated);
    }

    const renderTopRow = () => {
        if(!activeClassifications.length) {
            return (
                <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                </tr>
            )
        }
        
        return (
            <tr>
                <td style={{textAlign: 'center'}}>
                    <Radio
                        checked={noDefault}
                        label=""
                        labelClassName="display-block"
                        increaseArea="20%"
                        radioClass="iradio_square-blue"
                        value="no-default"
                        onChange={setDefault}
                    />
                </td>
                <td>Select...</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
            </tr>                                                
        )
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Classification Levels</h2>

                            <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                <strong>Classification levels</strong> and their corresponding <strong>distribution statements</strong> are applied to documents.
                                Each can be added to document templates using SMART Template placeholders.
                                Note: The Classification field (dropdown) will only be displayed if items are added below.
                            </div>

                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => handleSubmit(event)}>

                                <table className="table table-striped table-bordered table-hover dataTable" >
                                    <thead>
                                        <tr>
                                            <th style={{width: '20px'}}>Default</th>
                                            <th>Classification Level</th>
                                            <th>Distribution Statement</th>
                                            <th style={{width: '150px'}}>Action</th>
                                        </tr>
                                    </thead>
                                    <tfoot>
                                        <tr>
                                            <th>Default</th>
                                            <th>Classification Level</th>
                                            <th>Distribution Statement</th>
                                            <th>Action</th>
                                        </tr>
                                    </tfoot>
                                    <tbody>
                                        {renderTopRow()}
                                        {activeClassifications
                                            .map((obj, index) => (
                                                <tr key={obj.timestamp} className="animated fadeIn">
                                                    <td style={{textAlign: 'center'}}>
                                                        <Radio
                                                            checked={obj.is_default}
                                                            label=""
                                                            labelClassName="display-block"
                                                            increaseArea="20%"
                                                            radioClass="iradio_square-blue"
                                                            value={obj.timestamp}
                                                            onChange={setDefault}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input className="form-control" type="text" value={obj.name} onChange={(e) => updateMember(e, index, 'name')} required />
                                                    </td>
                                                    <td>
                                                        <TextArea className="form-control" name="description" value={obj.description} onChange={(e) => updateMember(e, index, 'description')} />
                                                    </td>
                                                    <td>
                                                        <button type="button" className="btn btn-xs" disabled={index === 0} onClick={() => moveMemberUp(obj)}><i className="fa fa-arrow-up"></i></button>
                                                        &nbsp;
                                                        <button type="button" className="btn btn-xs" disabled={index + 1 === activeClassifications.length} onClick={() => moveMemberDown(obj)}><i className="fa fa-arrow-down"></i></button>
                                                        &nbsp;
                                                        {obj.id ? (
                                                            <button type="button" className="btn btn-xs btn-light" onClick={() => archiveMember(obj)}>Archive</button>
                                                        ) : (
                                                            <button type="button" className="btn btn-xs btn-warning" onClick={() => deleteMember(obj)}>Delete</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                        <tr>
                                            <td>&nbsp;</td>
                                            <td>&nbsp;</td>
                                            <td>&nbsp;</td>
                                            <td><button type="button" className="btn btn-xs btn-primary" onClick={addMember}>Add</button></td>
                                        </tr>
                                        
                                    </tbody>
                                </table>
                                
                                <div className="hr-line-dashed"></div>

                                <div>
                                    <label className="control-label">Archived Classifications</label>
                                    <span className="help-block m-b-none m-t-none">
                                        <small className="form-info-text" > Archived Classifications are not available for further selection.</small>
                                    </span>

                                    <ul className="list-group clear-list m-t">
                                        {archivedClassifications.map(item => (
                                                <li className="list-group-item" key={item.timestamp}>
                                                    <button type="button" className="btn btn-xs btn-light" onClick={() => restoreMember(item)}>Restore</button>
                                                                &nbsp;
                                                    {item.name}
                                                </li>
                                            ))
                                        }
                                        {!archivedClassifications.length && (
                                            <li className="list-group-item">Nothing Archived</li>
                                        )}
                                    </ul>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />
                                </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default Classifications
